import { useState } from "react"
import { ChevronRight, ChevronDown } from "lucide-react"
import { ActionMeasureItem } from "./ActionMeasureItem"
import { type MeasureInfo, type ActionCategory } from "../app/services/simulationCreatorApi"

type ActionMeasureTreeProps = {
  actions: ActionCategory[]
  measures: MeasureInfo[]
  onAddMeasure: (actionPath: string, measureId: number) => void
  onRemoveMeasure: (actionPath: string, measureId: number) => void
  parentPath?: string
  depth?: number
}

type TreeNodeProps = {
  action: ActionCategory
  actionPath: string
  measures: MeasureInfo[]
  onAddMeasure: (actionPath: string, measureId: number) => void
  onRemoveMeasure: (actionPath: string, measureId: number) => void
  depth: number
}

const TreeNode = ({
  action,
  actionPath,
  measures,
  onAddMeasure,
  onRemoveMeasure,
  depth
}: TreeNodeProps) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const hasChildren = action.categories && action.categories.length > 0
  const isLeaf = !hasChildren

  return (
    <div className="relative">
      {/* Connection line for nested items */}
      {depth > 0 && (
        <div
          className="absolute left-0 top-0 w-px bg-gray-300"
          style={{
            height: "calc(100% - 12px)",
            marginTop: "12px"
          }}
        />
      )}

      <div className={`flex items-start ${depth > 0 ? "ml-4" : ""}`}>
        {/* Expand/Collapse button for categories */}
        {hasChildren ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="shrink-0 p-1 hover:bg-gray-200 rounded mr-1 mt-1"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
          </button>
        ) : (
          <div className="shrink-0 w-6 mr-1" /> // Spacer for alignment
        )}

        {/* Action item */}
        <div className="flex-1 min-w-0">
          <ActionMeasureItem
            action={action}
            actionPath={actionPath}
            measures={measures}
            onAddMeasure={onAddMeasure}
            onRemoveMeasure={onRemoveMeasure}
            isLeaf={isLeaf}
          />
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className={`mt-1 ${depth > 0 ? "ml-4" : ""}`}>
          {action.categories!.map((child, index) => (
            <TreeNode
              key={`${actionPath}.categories.${index}`}
              action={child}
              actionPath={`${actionPath}.categories.${index}`}
              measures={measures}
              onAddMeasure={onAddMeasure}
              onRemoveMeasure={onRemoveMeasure}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export const ActionMeasureTree = ({
  actions,
  measures,
  onAddMeasure,
  onRemoveMeasure,
  parentPath = "",
  depth = 0
}: ActionMeasureTreeProps) => {
  if (!actions || actions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No actions to display. Generate actions first.
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {actions.map((action, index) => {
        const currentPath = parentPath ? `${parentPath}.categories.${index}` : `${index}`
        return (
          <TreeNode
            key={currentPath}
            action={action}
            actionPath={currentPath}
            measures={measures}
            onAddMeasure={onAddMeasure}
            onRemoveMeasure={onRemoveMeasure}
            depth={depth}
          />
        )
      })}
    </div>
  )
}

export default ActionMeasureTree
